// Portal do Fornecedor (api/FornecedorPortal) — o fornecedor é sempre resolvido pelo
// usuário logado no backend, nunca por parâmetro.

export interface FornecedorOsVm {
  ordemServicoId: string
  statusId: number
  descricaoStatus: string
  dataInicio: string
  horaInicio?: string | null
  dataFinal: string
  horaFinal?: string | null
  valorTotal?: number | null
  // Numericamente igual a valorTotal (mesma coluna no banco) — não exibir como valor à parte.
  valorCompra?: number | null
  nomeConsultorOperacional?: string | null
  obsFornecedorOperador?: string | null
  prazoPagamento?: number | null
  obsFornecedor?: string | null
  temMotoristaAtribuido: boolean
  temVeiculoAtribuido: boolean
  comprasStatusId: number
  // Preenchido só quando statusId é 13 (Em Reajuste) — motivo da recusa do operador.
  motivoReajuste?: string | null
  // Trecho base da OS (não do passageiro individual) — já vem formatado (origem → destino) em uma string só.
  enderecoCompleto?: string | null
  // null quando não tem atribuição — temMotoristaAtribuido/temVeiculoAtribuido continuam a fonte de verdade do status.
  motorista?: FornecedorMotoristaAtribuidoVm | null
  veiculo?: FornecedorVeiculoAtribuidoVm | null
  // Já vem embutido no listpaginated/GetById — não precisa de chamada extra.
  passageiros?: FornecedorPassageiroVm[] | null
  // Só vem preenchido na resposta de atribuir motorista/veículo; no GET normal é sempre [].
  avisos?: string[] | null
}

export interface FornecedorMotoristaAtribuidoVm {
  id: number
  nome: string
  cpf: string
}

export interface FornecedorVeiculoAtribuidoVm {
  id: string
  placa: string
  modelo: string
  ano?: number | null
}

export interface FornecedorPassageiroVm {
  ordemServicoId: string
  nome: string
  documento?: string | null
  contato?: string | null
  data?: string | null
  horario?: string | null
  companhia?: string | null
  voo?: string | null
  // Prontos pra exibir — quando é aeroporto, a sigla já vem na frente separada por "|".
  enderecoOrigem?: string | null
  enderecoDestino?: string | null
}

export interface FornecedorMotoristaVm {
  id: number
  nome: string
  cpf: string
  contato: string
  numeroRegistro?: string | null
  categoria?: string | null
  validade?: string | null
  // Flags de vencimento já calculadas no backend — não parsear data nem fazer conta no front.
  cnhVencida?: boolean
  cnhProximaDoVencimento?: boolean
  diasParaVencimentoCnh?: number | null
  antecedentesCriminais?: string | null
  antecedentesValidade?: string | null
  antecedentesVencido?: boolean
  antecedentesProximoDoVencimento?: boolean
  vinculadoAMim: boolean
}

export class FornecedorMotoristaRegister {
  public nome = ''
  public cpf = ''
  public contato = ''
  public tipoContatoId = 4 // default Celular
  public numeroRegistro = ''
  public categoria = ''
  public validade = '' // dd/MM/yyyy — obrigatória
  public antecedentesCriminais = ''
  public antecedentesValidade = '' // dd/MM/yyyy
}

// POST /motoristas/{motoristaId}/edit — mesmos campos do cadastro, sem nome/cpf (identidade não muda)
export class FornecedorMotoristaEdit {
  public contato = ''
  public tipoContatoId = 4
  public numeroRegistro = ''
  public categoria = ''
  public validade = ''
  public antecedentesCriminais = ''
  public antecedentesValidade = ''
}

// Shape restrito devolvido pela listagem — diferente do VeiculoVmGeral devolvido no cadastro
export interface FornecedorVeiculoVm {
  id: string
  placa: string
  modelo: string
  cor?: string | null
  // Uso interno do portal só — nunca exibir em Excel/PDF voltado ao cliente final.
  ano?: number | null
  crlv?: string | null
  seguro?: string | null
  seguroValidade?: string | null
  seguroVencido?: boolean
  seguroProximoDoVencimento?: boolean
  diasParaVencimentoSeguro?: number | null
  // NOVO — tipo agora mora no veículo, não no modelo; vem preenchido pela API.
  tipoVeiculoId?: number | null
  tipoVeiculoDescricao?: string | null
}

export class FornecedorVeiculoRegister {
  public veiculoModeloId = 0
  // NOVO — obrigatório: sem ele a API responde 400 "Tipo do veículo é obrigatório".
  public tipoVeiculoId = 0
  public placa = ''
  public cor = ''
  public ano: number | null = null // opcional — entre 1950 e ano atual+1
  public crlv = ''
  public seguro = ''
  public seguroValidade = '' // dd/MM/yyyy
}

// POST /veiculos/{veiculoId}/edit — mesmos campos do cadastro, sem placa (identidade não muda)
export class FornecedorVeiculoEdit {
  public veiculoModeloId = 0
  // NOVO — obrigatório, igual ao cadastro.
  public tipoVeiculoId = 0
  public cor = ''
  public ano: number | null = null
  public crlv = ''
  public seguro = ''
  public seguroValidade = ''
}

// Shape completo devolvido pelo POST /veiculos — não usar pra alimentar a listagem restrita
export interface VeiculoVmGeral {
  id: string
  placa: string
  modelo?: string | null
  tipoVeiculoId?: number | null
  descricaoTipoVeiculo?: string | null
  isFrota?: string | null
  fornecedorId?: number | null
  cnpj?: string | null
  razaoSocial?: string | null
  [key: string]: any
}

// Documentos de motorista/veículo — endpoints .../documentos, .../documentos/upload etc.
// TipoDocId deve ser resolvido pela descrição via TipoDocumento/listall, nunca fixado por
// número: o id em qa (CNH 3, Antecedentes 8, Licenciamento 5, Seguro 6) não é garantido em prod.
export interface FornecedorDocumentoVm {
  id: number
  nomeArquivo: string
  descricao: string
  dataCriacao: string
  tipoDocId: number
  tipoDocDescricao: string
  pessoaId?: number
}

export class FornecedorDocumentoUpload {
  public Descricao = ''
  public TipoDocId = 0
  public Arquivo: File | null = null

  public toFormData(): FormData {
    const formData = new FormData()
    formData.append('Descricao', this.Descricao)
    formData.append('TipoDocId', this.TipoDocId.toString())
    if (this.Arquivo) {
      formData.append('Arquivo', this.Arquivo)
    }
    return formData
  }
}

// Cotação pelo fornecedor (api/FornecedorPortalCotacao) — cada linha é um trecho que a TG
// mandou este fornecedor cotar. O VM é um subconjunto explícito: não traz cliente, subcliente,
// consultores, nem qualquer linha de fornecedor concorrente convidado para a mesma solicitação.
export interface FornecedorCotacaoPortalVm {
  itemCotacaoId: string
  solicitacaoId: number
  codigoTg?: string | null
  nomeEvento?: string | null
  dataInicio?: string | null
  dataFinal?: string | null
  origem?: string | null
  destino?: string | null
  descricaoTipoServico?: string | null
  descricaoServico?: string | null
  descricaoTipoVeiculo?: string | null
  descricaoTipoMotorista?: string | null
  observacao?: string | null
  // Quantidade de passageiros do trecho (item_proposta.quantidade_passageiros).
  pax: number
  // Status do trecho (status_cotacao_item) — ver supplier-portal-quote-status.util.ts.
  statusId: number
  descricaoStatus?: string | null
  // Status do envio (cabeçalho fornecedor_cotacao). Só 3 aceita resposta — não confundir
  // com statusId: um trecho pode estar em 1 com o cabeçalho já finalizado em 4.
  cotacaoStatusId: number
  // Zerados enquanto o trecho não foi respondido.
  custo: number
  custoHoraExtra: number
  custoKmExtra: number
  // Preenchidos só quando o trecho foi recusado (statusId 5).
  motivoRecusaDescricao?: string | null
  obsRecusa?: string | null
  [key: string]: any
}

export class FornecedorCotacaoValorEnvio {
  public custo = 0
  public custoHoraExtra = 0
  public custoKmExtra = 0
  // Opcional, máx. 500. Quando vem, vira a primeira observação do trecho, gravada junto com o valor.
  public observacao: string | null = null
}

// Observação que o fornecedor deixou no trecho pelo portal. Mesmo objeto no portal e na tela do
// operador (observacoesFornecedor): só o que o próprio fornecedor escreveu, nada de concorrente.
export interface FornecedorCotacaoObservacaoVm {
  id: number
  itemCotacaoId: string
  observacao: string
  dataCriacao?: string | null
  horaCriacao?: string | null
  nomeCriadoPor?: string | null
}

// Origem, paradas e destino têm exatamente os mesmos campos no backend — uma interface serve aos três.
export interface FornecedorCotacaoItinerarioPontoVm {
  id: number
  solicitacaoId: number
  localParada?: string | null
  cep?: string | null
  logradouro?: string | null
  numero?: string | null
  bairro?: string | null
  complemento?: string | null
  cidade?: string | null
  estado?: string | null
  // 'O' origem, 'D' destino, qualquer outro valor é parada intermediária.
  trecho?: string | null
  horaParada?: string | null
  tempoPermanencia?: string | null
  atividades?: string | null
  [key: string]: any
}

export interface FornecedorCotacaoItinerarioVm {
  origem?: FornecedorCotacaoItinerarioPontoVm | null
  paradas?: FornecedorCotacaoItinerarioPontoVm[] | null
  destino?: FornecedorCotacaoItinerarioPontoVm | null
}
